import {
  BarChartData,
  BarChartLayout,
  BarChartLegend,
  BarChartShape,
  BarChartXAxis,
} from "Components/Chart/BarChart/barChartInterface";
import {
  AdviceProductDetailsFormValues,
  WorkSituation,
} from "Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import {
  CoverageNeed,
  ProductCoverageName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  isAkaGroupLifeIncapacitySelected,
  isAkaGroupLifeTimeSelected,
  isDisabilityInsuranceSelected,
} from "Store/akaAdvice/mappers/mapToCartProducts/mapToCartProducts";

import { getChartSize } from "Components/Chart/chartConfig";
import { VALUE_TYPE } from "Components/NumberValue";
import { getPriceFormatFromValue } from "Formatting/index";
import { getPayment } from "Store/akaAdvice/interfaces/getters";
import { getAge } from "Utils/dateUtils";
import { WindowSize } from "Utils/screenSizeUtils";
import { parse } from "date-fns";
import { FormikProps } from "formik";
import { AkaAdvicePriceFormValues } from "../../interfaces";

export const getDisabilityYearlyChartData = (
  formProps: FormikProps<AkaAdvicePriceFormValues>,
  need: CoverageNeed,
  adviceDetails: AdviceProductDetailsFormValues,
  baseAmount: number,
  customerDateOfBirth: string | undefined,
): BarChartData[] => {
  const effectivePaymentRateAkaGroupLifeIncapacity = getPayment(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK,
  ).effectivePaymentRate;
  const effectivePaymentRateAkaGroupLifeTime = getPayment(
    need,
    ProductCoverageName.AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST,
  ).effectivePaymentRate;
  const effectivePaymentRateDisabilityFlex = getPayment(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  ).effectivePaymentRate;

  const {
    incapacityOfWorkGLevel,
    timeLimitedDisabilityInterestGLevel,
    disabilityInsuredSumSlider,
  } = formProps.values;
  const sicknessPayout = need.existingCoverages.SICKNESS_PAYOUT;
  const navPayout = need.existingCoverages.NAV_PAYOUT;
  const employerPayout = need.existingCoverages.EMPLOYER_PAYOUT;

  const years = customerDateOfBirth
    ? String(67 - getAge(parse(customerDateOfBirth, "yyyy-MM-dd", new Date())))
    : "";
  const xAxisCoordinates = [0, 1, 2, 3, 4, 5, 6, years];

  const disabilityFlexAdviceValue = isDisabilityInsuranceSelected(
    formProps.values,
  )
    ? (disabilityInsuredSumSlider ?? 0) * effectivePaymentRateDisabilityFlex
    : 0;
  const adviceValueTimeLimited = isAkaGroupLifeTimeSelected(formProps.values)
    ? Number(timeLimitedDisabilityInterestGLevel) *
      baseAmount *
      effectivePaymentRateAkaGroupLifeTime
    : 0;
  const adviceValueInsuredIncapacity = isAkaGroupLifeIncapacitySelected(
    formProps.values,
  )
    ? Number(incapacityOfWorkGLevel) *
      baseAmount *
      effectivePaymentRateAkaGroupLifeIncapacity
    : 0;

  const barChartData: BarChartData[] = [];

  barChartData.push(getMarker("Inntekt uten ekstra forsikring", "#29412F"));
  barChartData.push(getMarker("Dagens inntekt", "#DF2928"));

  if (sicknessPayout > 0) {
    barChartData.push({
      x: xAxisCoordinates,
      y: [sicknessPayout].concat(Array(7).fill(0)),
      hovertemplate: [
        getPriceFormatFromValue(sicknessPayout, VALUE_TYPE.NOK),
      ].concat(Array(7).fill("")),
      name:
        adviceDetails.workSituation === WorkSituation.SELF_EMPLOYED
          ? "NAV sykepenger (NAV utbetaler 80 % av<br>sykepengegrunnlaget fra 17. dag)"
          : "NAV sykepenger (NAV utbetaler sykepenger inntil 6 G.<br>Noen arbeidsgivere dekker sykepenger opptil ordinær lønn)",
      marker: {
        color: "#C7AA7F",
      },
      yaxis: "y",
    });
  }
  if (navPayout > 0) {
    barChartData.push({
      x: xAxisCoordinates,
      y: [0].concat(Array(7).fill(navPayout)),
      hovertemplate: [""].concat(
        Array(7).fill(getPriceFormatFromValue(navPayout, VALUE_TYPE.NOK)),
      ),
      name: "NAV uføretrygd",
      marker: {
        color: "#957851",
      },
      yaxis: "y",
    });
  }
  if (employerPayout > 0) {
    barChartData.push({
      x: xAxisCoordinates,
      y: [0].concat(Array(7).fill(employerPayout)),
      hovertemplate: [""].concat(
        Array(7).fill(getPriceFormatFromValue(employerPayout, VALUE_TYPE.NOK)),
      ),
      name: "Arbeidsgivers forsikring",
      marker: {
        color: "#F5E4C7",
      },
    });
  }
  if (disabilityFlexAdviceValue > 0) {
    barChartData.push({
      x: xAxisCoordinates,
      y: [0].concat(Array(7).fill(disabilityFlexAdviceValue)),
      hovertemplate: [""].concat(
        Array(7).fill(
          getPriceFormatFromValue(disabilityFlexAdviceValue, VALUE_TYPE.NOK),
        ),
      ),
      name: "Individuell uføreforsikring (reguleres ikke i utbetalingsperioden)",
      marker: {
        color: "#003F4A",
      },
    });
  }
  if (adviceValueTimeLimited > 0) {
    const adviceValueTimeLimitedFormatted = getPriceFormatFromValue(
      adviceValueTimeLimited,
      VALUE_TYPE.NOK,
    );
    barChartData.push({
      x: xAxisCoordinates,
      y: [
        0,
        adviceValueTimeLimited,
        adviceValueTimeLimited,
        adviceValueTimeLimited,
        adviceValueTimeLimited,
        0,
        0,
        0,
      ],
      hovertemplate: [
        "",
        adviceValueTimeLimitedFormatted,
        adviceValueTimeLimitedFormatted,
        adviceValueTimeLimitedFormatted,
        adviceValueTimeLimitedFormatted,
        "",
        "",
        "",
      ],
      name: "A+ Gruppeliv tidsbegrenset uførerente",
      marker: {
        color: "#7ACDDC",
      },
      yaxis: "y",
    });
  }
  if (adviceValueInsuredIncapacity > 0) {
    barChartData.push({
      x: xAxisCoordinates,
      y: [0, 0, 0, 0, adviceValueInsuredIncapacity, 0, 0, 0],
      hovertemplate: [
        "",
        "",
        "",
        "",
        getPriceFormatFromValue(adviceValueInsuredIncapacity, VALUE_TYPE.NOK),
        "",
        "",
        "",
      ],
      name: "A+ Gruppeliv uførekapital (engangsutbetaling)",
      marker: {
        color: "#2B412E",
      },
      yaxis: "y",
    });
  }

  return barChartData;
};

export const getDisabilityYearlyChartLayout = (
  windowSize: WindowSize,
  need: CoverageNeed,
  netIncome: number,
): BarChartLayout => {
  const navPayout = need.existingCoverages.NAV_PAYOUT;
  const employerPayout = need.existingCoverages.EMPLOYER_PAYOUT;
  const totalPayout = navPayout + employerPayout;
  return {
    title: "Årlig utbetaling fra sykmelding <br> til pensjonsalder",
    size: getChartSize(windowSize, 500),
    xAxis,
    yAxis: { title: "Inntekt", nticks: 5, showline: true, showgrid: false },
    legend,
    shapes: getShapes(netIncome, totalPayout),
  };
};

const xAxis: BarChartXAxis = {
  title: "Antall år fra sykmelding",
  type: "category",
};

const legend: BarChartLegend = {
  orientation: "h",
  y: -0.3,
};

const getShapes = (income: number, totalPayout: number): BarChartShape[] => [
  {
    type: "line",
    xref: "x",
    yref: "paper",
    x: { start: 6.5, end: 6.5 },
    y: { start: 0, end: 1 },
    line: {
      width: 2,
      color: "#666666",
      dash: "dot",
    },
  },
  {
    type: "line",
    xref: "paper",
    yref: "y",
    x: { start: 0, end: 1 },
    y: { start: income, end: income },
    line: {
      width: 2,
      color: "#DF2928",
    },
  },
  {
    type: "line",
    xref: "paper",
    yref: "y",
    x: { start: 0, end: 1 },
    y: { start: totalPayout, end: totalPayout },
    line: {
      width: 2,
      color: "#29412F",
    },
  },
];

const getMarker = (name: string, color: string): BarChartData => ({
  x: [null],
  y: [null],
  mode: "markers",
  name,
  marker: {
    size: 10,
    symbol: "line-ew",
    line: { color, width: 1 },
    opacity: 0.5,
  },
});
